import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useState } from "react";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { propBoolField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propInt, propLength, propLengthMax, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { createListStore, UseListStore } from "../../common/listStore";
import { InventoryArticleDto, InventoryArticlePackageItemDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { map, distinctUntilChanged } from "rxjs";
import shallow from "zustand/shallow";
import { FormSection } from "../../common/FormSection";
import { renderListStore } from "../../common/listStoreUi";

export const InventoryArticleDialog = (props: {
    close: () => void;
    article: InventoryArticleEditDto | null;
    useArticlesStore: UseListStore<InventoryArticleDto>;
    onChange(): void;
}) => {
    const [useStore] = useState(() => createEntityStore<InventoryArticleEditDto>({
        data: props.article ?? newInventoryArticle(),
        path: "articles/inventoryArticles",
        id: props.article != null ? props.article.id : null,
        getNewId: p => p.id!,
        onSaved() {
            props.onChange();
            props.close();
        },
        propValidators: [
            propRequired("code"), propLength("code", 5, 5),
            propValueValidator("code", (p, e) =>
                props.useArticlesStore.getState().items.filter(i => i.id !== e.id).find(i => i.code === p) &&
                "Code already exists."),
            propRequired("nme"),
            propLengthMax("nme", 50),
            propValidValue("wt", "wtFormatted"), propRequired("wt"), propInt("wt"), propMinValue("wt", 0),
            propValueValidator("tarCode", (p, e) =>
                p.length > 0 && p.length !== 8 && p.length !== 10 &&
                "Tariff code must be 8 or 10 characters long."),
            propLength("origCountry", 2, 2),
        ],
    }));

    const { isPost, isPackage } = useStore(p => ({ isPost: p.clientData.isPost, isPackage: p.clientData.packageItems.length > 0 }));

    const [usePackageItemsStore] = useState(() => createListStore<InventoryArticlePackageItemDto>({
        data: useStore.getState().clientData$.pipe(
            map(p => ({ items: p.data.packageItems, isFetching: p.isFetching })),
            distinctUntilChanged(shallow),
        ),
        listProps: [
            { column: "articleCode", label: "Code", width: 80 },
            { column: "articleName", label: "Name", flex: 1 },
            { column: "quantity", label: "Quantity", width: 80, type: "number" },
        ],
        itemsPerPageMax: 5,
    }));

    return <>
        <DialogTitle>{props.article?.id == null ? "New inventory article" : `Edit inventory article`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid item xs={12}>{propTextField(useStore)("code", "Code")}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("nme", "Name")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("tarCode", "Tariff code")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("wt", "wtFormatted", "Weight", { unit: "g" })}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("origCountry", "Country of origin")}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("isPost", "Postage fee")}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("status", "Status")}</Grid>
                {!isPost && isPackage && <Grid item xs={12}><FormSection label="Package items">
                    {renderListStore(usePackageItemsStore, { hideFooter: true })}
                </FormSection></Grid>}
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};

interface InventoryArticleEditDto {
    id: number | null;
    code: string;
    nme: string;
    origCountry: string;
    tarCode: string;
    isPost: boolean;
    wt: number;
    wtFormatted?: string;
    status: string;
    packageItems: InventoryArticlePackageItemDto[];
}

const newInventoryArticle: () => InventoryArticleEditDto = () => ({
    id: null,
    code: "",
    nme: "",
    origCountry: "",
    tarCode: "",
    isPost: false,
    wt: 0,
    status: "Ongoing",
    packageItems: [],
});
